import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { format } from "date-fns";
import { Pencil, Plus, Trash2, UserMinus, UserCheck } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useRemoveWorker, useToggleWorkerActive } from "@/hooks/usePersonnelProjects";
import { AddWorkerDialog } from "./AddWorkerDialog";
import { EditWorkerDialog } from "./EditWorkerDialog";
import { toast } from "sonner";
import type { PersonnelProject, RateSchedule, TMWorker } from "@/hooks/usePersonnelProjects";

interface Props {
  project: PersonnelProject;
  workers: TMWorker[];
  rates: RateSchedule[];
}

export function PersonnelWorkersTab({ project, workers, rates }: Props) {
  const isMobile = useIsMobile();
  const [addOpen, setAddOpen] = useState(false);
  const [editingWorker, setEditingWorker] = useState<TMWorker | null>(null);
  const removeWorker = useRemoveWorker();
  const toggleActive = useToggleWorkerActive();

  const handleRemove = async (w: TMWorker) => {
    try {
      await removeWorker.mutateAsync({ workerId: w.id, projectId: project.id });
      toast.success(`${w.worker_name} removed`);
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  const handleToggle = async (w: TMWorker) => {
    try {
      await toggleActive.mutateAsync({ workerId: w.id, isActive: !w.is_active, projectId: project.id });
      toast.success(`${w.worker_name} ${w.is_active ? "deactivated" : "activated"}`);
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  return (
    <div className="space-y-4 mt-4">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold">Workers ({workers.length})</h2>
        <Button size="sm" onClick={() => setAddOpen(true)}>
          <Plus className="h-4 w-4 mr-1" /> Add Worker
        </Button>
      </div>

      {workers.length === 0 ? (
        <p className="text-center py-8 text-muted-foreground">No workers assigned</p>
      ) : isMobile ? (
        <div className="space-y-3">
          {workers.map((w) => {
            const rate = rates.find((r) => r.id === w.rate_schedule_id);
            const effectiveBill = w.bill_rate_override ?? rate?.bill_rate_regular ?? 0;
            const effectiveCost = w.cost_rate_override ?? rate?.cost_rate ?? 0;
            return (
              <Card key={w.id}>
                <CardContent className="p-4 space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium">{w.worker_name}</span>
                    <Badge variant="outline" className={w.is_active ? "bg-emerald-500/10 text-emerald-700" : "bg-muted text-muted-foreground"}>
                      {w.is_active ? "Active" : "Inactive"}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <span>{rate?.role_name ?? "—"}</span>
                    <span>${effectiveBill}/HR | ${effectiveCost}/HR</span>
                  </div>
                  {w.start_date && (
                    <p className="text-xs text-muted-foreground">Started {format(new Date(w.start_date), "MMM d")}</p>
                  )}
                  <div className="flex flex-wrap gap-2 pt-1">
                    <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setEditingWorker(w)}>
                      <Pencil className="mr-1 h-3 w-3" /> Edit
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => handleToggle(w)}>
                      {w.is_active ? <><UserMinus className="mr-1 h-3 w-3" />Deactivate</> : <><UserCheck className="mr-1 h-3 w-3" />Activate</>}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 text-xs text-destructive hover:text-destructive"
                      onClick={() => handleRemove(w)}
                      disabled={removeWorker.isPending}
                    >
                      <Trash2 className="mr-1 h-3 w-3" /> Remove
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Bill Rate</TableHead>
                  <TableHead>Cost Rate</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Start</TableHead>
                  <TableHead className="w-28" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {workers.map((w) => {
                  const rate = rates.find((r) => r.id === w.rate_schedule_id);
                  const effectiveBill = w.bill_rate_override ?? rate?.bill_rate_regular ?? 0;
                  const effectiveCost = w.cost_rate_override ?? rate?.cost_rate ?? 0;
                  return (
                    <TableRow key={w.id} className={!w.is_active ? "opacity-50" : ""}>
                      <TableCell className="font-medium">{w.worker_name}</TableCell>
                      <TableCell>{rate?.role_name ?? "—"}</TableCell>
                      <TableCell>${effectiveBill}/HR</TableCell>
                      <TableCell className="text-muted-foreground">${effectiveCost}/HR</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={w.is_active ? "bg-emerald-500/10 text-emerald-700" : "bg-muted text-muted-foreground"}>
                          {w.is_active ? "Active" : "Inactive"}
                        </Badge>
                      </TableCell>
                      <TableCell>{w.start_date ? format(new Date(w.start_date), "MMM d") : "—"}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button size="icon" variant="ghost" className="h-7 w-7" title="Edit worker" onClick={() => setEditingWorker(w)}>
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button size="icon" variant="ghost" className="h-7 w-7" title={w.is_active ? "Deactivate" : "Activate"} onClick={() => handleToggle(w)}>
                            {w.is_active ? <UserMinus className="h-3.5 w-3.5 text-muted-foreground" /> : <UserCheck className="h-3.5 w-3.5 text-emerald-600" />}
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7 text-muted-foreground hover:text-destructive"
                            title="Remove"
                            onClick={() => handleRemove(w)}
                            disabled={removeWorker.isPending}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <AddWorkerDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        personnelProjectId={project.id}
        rates={rates}
        existingWorkerNames={new Set(workers.map((w) => w.worker_name.toLowerCase().trim()))}
      />

      <EditWorkerDialog
        open={!!editingWorker}
        onOpenChange={(open) => {
          if (!open) setEditingWorker(null);
        }}
        worker={editingWorker}
        rates={rates}
        projectId={project.id}
      />
    </div>
  );
}
